import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Button, Dropdown, Space, Drawer, message } from 'antd';
import { UserOutlined, LogoutOutlined, CustomerServiceOutlined, MenuOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../api';

const { Header, Content, Footer } = Layout;

const FooterContactButtons = ({ onFallback }) => {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    api.get('/settings/site-info').then(res => {
      if (res.code === 200) setUrls(res.data || {});
    }).catch(() => {});
  }, []);

  const buttons = [
    { label: '工作室专属客服', key: 'footer_btn_studio', bg: 'linear-gradient(135deg, #ff6b6b, #ee5a24)', border: '#ff6b6b' },
    { label: '定制策略', key: 'footer_btn_custom', bg: 'linear-gradient(135deg, #ffd700, #f0932b)', border: '#ffd700' },
    { label: '成为EA创作者', key: 'footer_btn_creator', bg: 'linear-gradient(135deg, #00f3ff, #0984e3)', border: '#00f3ff' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
      {buttons.map(btn => (
        <span key={btn.key} onClick={() => {
          const url = urls[btn.key];
          if (url) window.open(url, '_blank');
          else onFallback();
        }} style={{
          background: 'none', border: 'none', color: '#8b9bb4',
          padding: 0, cursor: 'pointer', fontSize: 13,
          textAlign: 'left', transition: 'color 0.2s', lineHeight: 1.8
        }}
        onMouseEnter={e => e.target.style.color = btn.border}
        onMouseLeave={e => e.target.style.color = '#8b9bb4'}
        >{btn.label}</span>
      ))}
    </div>
  );
};

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const info = localStorage.getItem('user_info');
    if (info) setUser(JSON.parse(info));
  }, [location]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch (e) {}
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_info');
    setUser(null);
    navigate('/');
    message.success('已退出登录');
  };

  const handleContactService = async () => {
    try {
      const res = await api.get('/settings/customer-service');
      if (res.data?.url) {
        window.open(res.data.url, '_blank');
      } else {
        message.info('客服地址未配置，请联系管理员');
      }
    } catch (e) {
      message.error('获取客服地址失败');
    }
  };

  const navItems = [
    { key: '/', label: '首页' },
    { key: '/products', label: '产品中心' },
    { key: '/downloads', label: '下载中心' },
    { key: '/authorizations', label: '授权中心' },
    { key: '/faq', label: '常见问题' },
  ];

  const userMenuItems = [
    { key: 'user', label: '用户中心', icon: <UserOutlined />, onClick: () => navigate('/user') },
    { type: 'divider' },
    { key: 'logout', label: '退出登录', icon: <LogoutOutlined />, onClick: handleLogout },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#050509' }}>
      <Header className="main-header" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(5,5,9,0.85)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 40px', height: 64
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: '#00f3ff', letterSpacing: -1 }}>TIANQI</span>
          <span className="desktop-only" style={{ fontSize: 14, color: '#8b9bb4', letterSpacing: 2 }}>天启智汇</span>
        </Link>

        {/* 桌面端导航 - CSS隐藏移动端 */}
        <div className="desktop-only" style={{ alignItems: 'center', gap: 4 }}>
          {navItems.map(item => (
            <Link key={item.key} to={item.key} style={{
              padding: '6px 16px', borderRadius: 6, fontSize: 14, textDecoration: 'none',
              color: location.pathname === item.key ? '#00f3ff' : '#8b9bb4',
              background: location.pathname === item.key ? 'rgba(0,243,255,0.08)' : 'transparent',
              transition: 'all 0.2s', whiteSpace: 'nowrap'
            }}>{item.label}</Link>
          ))}
        </div>

        {/* 桌面端右侧按钮 - CSS隐藏移动端 */}
        <div className="desktop-only" style={{ alignItems: 'center', gap: 12 }}>
          <Button type="text" size="small" onClick={handleContactService}
            style={{ color: '#ffd700', fontSize: 13 }}>
            <CustomerServiceOutlined /> 联系客服
          </Button>
          {user ? (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" style={{ color: '#00f3ff', border: '1px solid rgba(0,243,255,0.3)', borderRadius: 6 }}>
                <UserOutlined /> {user.username}
              </Button>
            </Dropdown>
          ) : (
            <Space size={8}>
              <Button type="text" onClick={() => navigate('/login')} style={{ color: '#8b9bb4' }}>登录</Button>
              <Button onClick={() => navigate('/register')} style={{
                background: 'linear-gradient(135deg, rgba(0,243,255,0.2), rgba(0,243,255,0.05))',
                border: '1px solid #00f3ff', color: '#00f3ff', borderRadius: 6
              }}>注册</Button>
            </Space>
          )}
        </div>

        {/* 移动端汉堡按钮 - CSS隐藏桌面端 */}
        <div className="mobile-only" style={{ alignItems: 'center' }}>
          <Button type="text" icon={<MenuOutlined style={{ fontSize: 20 }} />}
            onClick={() => setMobileMenuOpen(true)}
            style={{ color: '#fff', padding: '4px 8px' }} />
        </div>
      </Header>

      {/* 移动端 Drawer 导航 */}
      <Drawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        placement="left"
        styles={{
          wrapper: { width: 260 },
          body: { background: '#050509', padding: 0 },
          header: { background: '#050509', borderBottom: '1px solid rgba(255,255,255,0.08)' }
        }}
        title={<span style={{ color: '#00f3ff', fontWeight: 700, letterSpacing: -1 }}>TIANQI 天启智汇</span>}
        closeIcon={<CloseOutlined style={{ color: '#8b9bb4' }} />}
      >
        {/* 移动端导航项 */}
        <div style={{ padding: '8px 0' }}>
          {navItems.map(item => (
            <Link key={item.key} to={item.key}
              onClick={() => setMobileMenuOpen(false)}
              style={{
                display: 'block', padding: '14px 24px', fontSize: 15, textDecoration: 'none',
                color: location.pathname === item.key ? '#00f3ff' : '#c0ccd8',
                background: location.pathname === item.key ? 'rgba(0,243,255,0.06)' : 'transparent',
                borderLeft: location.pathname === item.key ? '3px solid #00f3ff' : '3px solid transparent',
                transition: 'all 0.2s'
              }}>{item.label}</Link>
          ))}
        </div>

        {/* 移动端用户区域 */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '16px 24px' }}>
          <Button block onClick={handleContactService} style={{ marginBottom: 10, color: '#ffd700', borderColor: 'rgba(255,215,0,0.4)', background: 'transparent' }}>
            <CustomerServiceOutlined /> 联系客服
          </Button>
          {user ? (
            <>
              <Button block onClick={() => { navigate('/user'); setMobileMenuOpen(false); }}
                style={{ marginBottom: 10, color: '#00f3ff', borderColor: 'rgba(0,243,255,0.4)', background: 'transparent' }}>
                <UserOutlined /> {user.username}
              </Button>
              <Button block onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                style={{ color: '#ff6b6b', borderColor: 'rgba(255,107,107,0.4)', background: 'transparent' }}>
                <LogoutOutlined /> 退出登录
              </Button>
            </>
          ) : (
            <Space style={{ width: '100%' }} direction="vertical">
              <Button block onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                style={{ color: '#8b9bb4', borderColor: 'rgba(255,255,255,0.15)', background: 'transparent' }}>
                登录
              </Button>
              <Button block onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                style={{ background: 'linear-gradient(135deg, rgba(0,243,255,0.2), rgba(0,243,255,0.05))', border: '1px solid #00f3ff', color: '#00f3ff' }}>
                注册
              </Button>
            </Space>
          )}
        </div>
      </Drawer>

      <Content style={{ minHeight: 'calc(100vh - 64px - 200px)' }}>
        <Outlet />
      </Content>

      <Footer className="main-footer" style={{
        background: 'rgba(5,5,9,0.95)', borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '48px 40px 24px'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 32, marginBottom: 40 }}>
            <div>
              <h4 style={{ color: '#00f3ff', marginBottom: 16, fontSize: 16 }}>天启智汇</h4>
              <p style={{ color: '#8b9bb4', fontSize: 13, lineHeight: 1.8 }}>
                量化产品成交与交付平台<br />专业·安全·高效
              </p>
            </div>
            <div>
              <h4 style={{ color: '#fff', marginBottom: 16, fontSize: 14 }}>功能入口</h4>
              {[
                { to: '/authorizations', text: '授权中心' },
                { to: '/register', text: '注册开通' },
                { to: '/downloads', text: '下载中心' },
              ].map(l => (
                <Link key={l.to} to={l.to} style={{ display: 'block', color: '#8b9bb4', fontSize: 13, marginBottom: 8, textDecoration: 'none' }}>{l.text}</Link>
              ))}
            </div>
            <div>
              <h4 style={{ color: '#fff', marginBottom: 16, fontSize: 14 }}>更多</h4>
              {[
                { to: '/products', text: '产品矩阵' },
                { to: '/faq', text: '常见问题' },
                { to: '/recharge-guide', text: '充值指南' },
                { to: '/disclaimer', text: '风险声明' },
              ].map(l => (
                <Link key={l.to} to={l.to} style={{ display: 'block', color: '#8b9bb4', fontSize: 13, marginBottom: 8, textDecoration: 'none' }}>{l.text}</Link>
              ))}
            </div>
            <div>
              <h4 style={{ color: '#fff', marginBottom: 16, fontSize: 14 }}>联系我们</h4>
              <p style={{ color: '#8b9bb4', fontSize: 13, cursor: 'pointer', marginBottom: 8 }} onClick={handleContactService}>
                <CustomerServiceOutlined /> 在线客服
              </p>
              <Link to="/contact" style={{ display: 'block', color: '#8b9bb4', fontSize: 13, marginBottom: 12, textDecoration: 'none' }}>联系方式</Link>
              <FooterContactButtons onFallback={handleContactService} />
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 20, textAlign: 'center' }}>
            <p style={{ color: '#555', fontSize: 12 }}>
              Copyright &copy; {new Date().getFullYear()} 天启智汇·Tianqi Intelligence. All rights reserved.
            </p>
            <Link to="/disclaimer" style={{ color: '#555', fontSize: 11, textDecoration: 'none' }}>风险免责声明</Link>
          </div>
        </div>
      </Footer>
    </Layout>
  );
};

export default MainLayout;
